import { Box, Tabs as MuiTabs } from '@mui/material';
import { LineChart as MUILineChart } from '@mui/x-charts';
import dayjs from 'dayjs';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  bucketPerDay,
  ChartGranularity,
  fittingGranularities,
} from '../../../helpers/chartTimeline';
import { useTranslation } from '../../../hooks/useTranslation';
import { AgentType } from '../../../interfaces/Agent';
import { DateRangeDate } from '../../../interfaces/Analytics';
import StyledTab from '../Tabs/StyledTab';

interface BarChartProps {
  agentType?: AgentType;
  callsPerMonth?: Record<string, number>;
  chatPerMonth?: Record<string, number>;
  // YYYY-MM-DD counts. Without them the chart stays on months.
  perDay?: Record<string, number>;
  dateRange?: [DateRangeDate, DateRangeDate];
}

type ZoomChoice = 'auto' | ChartGranularity;

const CHART_MARGIN = { top: 24, right: 18, bottom: 24, left: 42 };

interface YearMonth {
  year: number;
  month: number; // 1-12
}

// Call data keys are "MM-YYYY", chat data keys are "YYYY-MM".
const parseMonthKey = (key: string, agentType: AgentType): YearMonth | null => {
  const parts = key.split('-');
  if (parts.length !== 2) return null;

  const [first, second] = parts;
  const year = Number(agentType === AgentType.CALL ? second : first);
  const month = Number(agentType === AgentType.CALL ? first : second);

  if (!Number.isInteger(year) || !Number.isInteger(month)) return null;
  if (month < 1 || month > 12) return null;

  return { year, month };
};

const formatMonthKey = (
  { year, month }: YearMonth,
  agentType: AgentType
): string => {
  const monthNum = String(month).padStart(2, '0');
  return agentType === AgentType.CALL
    ? `${monthNum}-${year}`
    : `${year}-${monthNum}`;
};

const nextMonth = ({ year, month }: YearMonth): YearMonth =>
  month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 };

/**
 * Build the contiguous list of months spanning from the earliest to the
 * latest month present in the data (inclusive), so a date range that
 * crosses a year boundary produces a continuous timeline instead of being
 * collapsed into a single year.
 */
const buildTimeline = (months: YearMonth[]): YearMonth[] => {
  if (months.length === 0) {
    const now = new Date();
    return Array.from({ length: 12 }, (_, index) => ({
      year: now.getFullYear(),
      month: index + 1,
    }));
  }

  const sorted = [...months].sort((a, b) =>
    a.year === b.year ? a.month - b.month : a.year - b.year
  );
  const start = sorted[0];
  const end = sorted[sorted.length - 1];

  const timeline: YearMonth[] = [];
  let cursor = start;
  while (
    cursor.year < end.year ||
    (cursor.year === end.year && cursor.month <= end.month)
  ) {
    timeline.push(cursor);
    cursor = nextMonth(cursor);
  }
  return timeline;
};

const BarChart: React.FC<BarChartProps> = ({
  agentType = AgentType.CALL,
  callsPerMonth,
  chatPerMonth,
  perDay,
  dateRange,
}) => {
  const { t } = useTranslation();
  const [zoom, setZoom] = useState<ZoomChoice>('auto');

  // The zooms on offer depend on the room the chart has
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    setWidth(element.clientWidth);
    const observer = new ResizeObserver(([entry]) =>
      setWidth(entry.contentRect.width)
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const monthLabels = [
    t('Jan'),
    t('Feb'),
    t('Mar'),
    t('Apr'),
    t('May'),
    t('Jun'),
    t('Jul'),
    t('Aug'),
    t('Sep'),
    t('Oct'),
    t('Nov'),
    t('Dec'),
  ];

  const dataSource =
    agentType === AgentType.CALL ? callsPerMonth : chatPerMonth;

  const timeline = useMemo(() => {
    const months = Object.keys(dataSource ?? {})
      .map(key => parseMonthKey(key, agentType))
      .filter((entry): entry is YearMonth => entry !== null);
    return buildTimeline(months);
  }, [dataSource, agentType]);

  const monthXAxisLabels = useMemo(
    () =>
      timeline.map(entry => `${monthLabels[entry.month - 1]} ${entry.year}`),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [timeline]
  );

  const monthChartData = useMemo(
    () =>
      timeline.map(
        entry => dataSource?.[formatMonthKey(entry, agentType)] || 0
      ),
    [timeline, dataSource, agentType]
  );

  // The picked range, or the days the data covers when there is none
  const range = useMemo(() => {
    if (!perDay) return null;
    const days = Object.keys(perDay).sort();
    const start = dateRange?.[0] ?? (days.length ? dayjs(days[0]) : null);
    const end =
      dateRange?.[1] ?? (days.length ? dayjs(days[days.length - 1]) : null);
    if (!start?.isValid() || !end?.isValid() || end.isBefore(start)) {
      return null;
    }
    return { start, end };
  }, [perDay, dateRange]);

  const plotWidth = width - CHART_MARGIN.left - CHART_MARGIN.right;
  const fitting: ChartGranularity[] = range
    ? fittingGranularities(range.start, range.end, plotWidth)
    : ['month'];
  // A pick that no longer fits (smaller window, longer range) falls back to auto
  const activeZoom = zoom !== 'auto' && fitting.includes(zoom) ? zoom : 'auto';
  const granularity = activeZoom === 'auto' ? fitting[0] : activeZoom;

  const zoomed = useMemo(() => {
    if (granularity === 'month' || !perDay || !range) return null;
    const { starts, values } = bucketPerDay(
      perDay,
      granularity,
      range.start,
      range.end
    );
    const showYear = range.start.year() !== range.end.year();
    const labels = starts.map(
      day =>
        `${day.date()} ${monthLabels[day.month()]}${showYear ? ` ${day.year()}` : ''}`
    );
    return { labels, values };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [granularity, perDay, range]);

  const xAxisLabels = zoomed?.labels ?? monthXAxisLabels;
  const chartData = zoomed?.values ?? monthChartData;

  const yAxisLabel =
    agentType === AgentType.CALL ? t('Calls') : t('Conversations');

  return (
    <Box ref={containerRef}>
      {fitting.length > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <MuiTabs
            value={activeZoom}
            onChange={(_, value: ZoomChoice) => setZoom(value)}
            aria-label={t('Chart zoom')}
            sx={{
              p: 0.5,
              borderRadius: 999,
              bgcolor: '#FFFFFF',
              border: '1px solid rgba(15, 23, 42, 0.06)',
              boxShadow: '0px 8px 24px rgba(15, 23, 42, 0.08)',
              minHeight: 56,
              '& .MuiTabs-indicator': { display: 'none' },
            }}
          >
            <StyledTab value="auto" label={t('Auto')} />
            <StyledTab
              value="day"
              label={t('Day')}
              disabled={!fitting.includes('day')}
            />
            <StyledTab
              value="week"
              label={t('Week')}
              disabled={!fitting.includes('week')}
            />
            <StyledTab value="month" label={t('Month')} />
          </MuiTabs>
        </Box>
      )}
      <MUILineChart
        xAxis={[
          {
            id: 'chartCategories',
            data: xAxisLabels,
            scaleType: 'point',
            tickLabelStyle: { fill: '#7A80A8', fontSize: 14 },
            disableLine: true,
            disableTicks: true,
          },
        ]}
        yAxis={[
          {
            label: yAxisLabel,
            tickLabelStyle: { fill: '#98A2B3', fontSize: 12 },
            disableLine: true,
            disableTicks: true,
          },
        ]}
        grid={{ vertical: true, horizontal: false }}
        series={[
          {
            data: chartData,
            color: '#5F7EE8',
            area: true,
            showMark: false,
            curve: 'monotoneX',
          },
        ]}
        axisHighlight={{ x: 'line' }}
        margin={CHART_MARGIN}
        height={350}
        sx={{
          '& .MuiAreaElement-series-auto-generated-id-0': {
            fill: '#5F7EE8',
            fillOpacity: 0.12,
          },
          '& .MuiLineElement-series-auto-generated-id-0': {
            strokeWidth: 3.5,
          },
          '& .MuiChartsAxis-tickLabel': {
            fontFamily: 'var(--default-fonts)',
          },
          '& .MuiChartsGrid-line': {
            stroke: 'rgba(15, 23, 42, 0.14)',
            strokeDasharray: '6 6',
          },
          '& .MuiChartsAxis-line': {
            stroke: 'rgba(15, 23, 42, 0.08)',
          },
        }}
      />
    </Box>
  );
};

export default BarChart;
